import React from "react";
import { Mail, Phone, MapPin } from 'lucide-react';
import Header from "./Header";
import Footer from "./Footer";
import MobileFooter from './MobileFooter';
import { Card, Button, FormField, Input, Textarea, Eyebrow, Heading, Container } from '../ui';

const Contact = () => {
  return (
    <div className="min-h-screen bg-bg pb-20 lg:pb-0 font-sans">
      <Header />

      {/* Hero Section */}
      <div className="on-dark bg-navy py-12 sm:py-16">
        <Container className="text-center">
          {/* Icon above heading */}
          <div className="mb-4 flex items-center justify-center sm:mb-5">
            <div className="rounded-xl border border-line/15 bg-primary/10 p-3 sm:rounded-2xl sm:p-4">
              <Mail className="h-6 w-6 text-primary sm:h-8 sm:w-8" />
            </div>
          </div>
          <Eyebrow className="justify-center">Get in touch</Eyebrow>
          <Heading variant="display" className="mt-3">
            Contact <em className="kbc-serif">Us</em>
          </Heading>
          <p className="mx-auto mt-4 max-w-xl text-base text-text sm:text-lg">
            We're here to help! Reach out with any questions or feedback.
          </p>
        </Container>
      </div>

      {/* Main Content */}
      <Container className="max-w-6xl py-10 sm:py-12 md:py-16">
        {/* Contact Cards */}
        <div className="mb-12 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
          {/* Email */}
          <Card interactive className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Mail className="h-6 w-6" />
            </div>
            <h3 className="mb-2 font-display text-lg font-bold text-ink">Email Us</h3>
            <p className="mb-4 text-sm text-muted">admin@kbcmembers.org</p>
            <Button size="sm">Send Email</Button>
          </Card>

          {/* Call */}
          <Card interactive className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent/15 text-accent">
              <Phone className="h-6 w-6" />
            </div>
            <h3 className="mb-2 font-display text-lg font-bold text-ink">Call Us</h3>
            <p className="text-sm text-muted">7305056436</p>
            <p className="mb-3 text-xs text-muted">Mon-Fri: 9AM-6PM PST</p>
            <Button size="sm" variant="accent">Call Now</Button>
          </Card>

          {/* Visit */}
          <Card interactive className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-warning/15 text-warning">
              <MapPin className="h-6 w-6" />
            </div>
            <h3 className="mb-2 font-display text-lg font-bold text-ink">Visit Us</h3>
            <p className="mb-4 text-center text-sm text-muted">
              Kongu Business Community,<br />
              NammaOffice,<br />
              3rd Floor, Balaji Towers,<br />
              Seerangapalayam, Salem-636007
            </p>
            <Button
              size="sm"
              variant="ghost"
              as="a"
              href="https://maps.google.com/?q=3rd Floor, Balaji Towers, Seerangapalayam, Salem-636007"
              target="_blank"
              rel="noopener noreferrer"
            >
              Get Directions
            </Button>
          </Card>
        </div>

        {/* Form */}
        <Card padding="lg" className="mx-auto mb-12 max-w-2xl">
          <h2 className="mb-6 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
            Send Us a <em className="kbc-serif">Message</em>
          </h2>
          <form className="space-y-4">
            <FormField label="Full Name" required>
              <Input type="text" placeholder="Full Name" required />
            </FormField>
            <FormField label="Email Address" required>
              <Input type="email" placeholder="Email Address" required />
            </FormField>
            <FormField label="Subject" required>
              <Input type="text" placeholder="Subject" required />
            </FormField>
            <FormField label="Message" required>
              <Textarea placeholder="Message" required className="min-h-[120px]" />
            </FormField>
            <Button type="submit" size="lg">
              Send Message
            </Button>
            <p className="mt-2 text-sm text-muted">
              We'll get back to you within 24 hours
            </p>
          </form>
        </Card>

        {/* Bottom Section */}
        <div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-[1fr,300px]">
          {/* Social */}
          <div>
            <h3 className="mb-3 font-display text-xl font-bold text-ink">Connect With Us</h3>
            <div className="flex flex-wrap gap-3">
              <Button size="sm" variant="subtle">Facebook</Button>
              <Button size="sm" variant="subtle">Twitter</Button>
              <Button size="sm" variant="subtle">LinkedIn</Button>
              <Button size="sm" variant="subtle">Instagram</Button>
            </div>
          </div>

          {/* Business Hours */}
          <Card className="text-sm text-text">
            <h3 className="mb-3 font-display text-base font-bold text-ink">Business Hours</h3>
            <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
            <p>Saturday: 10:00 AM - 4:00 PM</p>
            <p>Sunday: Closed</p>
            <p className="mt-2 text-xs text-muted">
              *All times Pacific Standard Time <br />
              Emergency support available 24/7
            </p>
          </Card>
        </div>
      </Container>

      <Footer />
      <MobileFooter />
    </div>
  );
};

export default Contact;
